// viewEditorDraft.js

import { normalizeBoardLayout } from 'utilities/BoardLayout';
import { normalizeEnumOptionsViewSortMode } from 'utilities/EnumOptions';
import { generateKey } from 'utilities/Keys';
import {
  DEFAULT_SUBTITLE_FIELD_KEY,
  LEGACY_SUBTITLE_FIELD_KEY,
  getViewIncludeSubtitle,
} from 'utilities/NodeSubtitle';
import { normalizeFilterJoin } from './viewModalFilters';

export const DEFAULT_CHART_CONFIG = Object.freeze({
  chartType: 'bar',
  aggregation: 'count',
  xField: '',
  yField: '',
  groupField: '',
  showLegend: true,
  showGrid: true,
  fontFamily: 'inherit',
  backgroundColor: '',
  textColor: '',
  gridColor: '',
  yAxisFormat: 'number',
  currencyCode: 'USD',
  xDateBin: 'auto',
  seriesColors: {},
});

export const DEFAULT_SCHEDULER_CONFIG = Object.freeze({
  scheduledType: '',
  scheduledField: '',
  slotIncrementMinutes: '15',
  scheduledFilters: [],
  scheduledProperties: [],
});

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTimelinePreset(value) {
  return String(value || '').trim().toLowerCase() === 'gantt' ? 'gantt' : 'timeline';
}

function resolveTimelinePresetFlags(source = {}) {
  const preset = normalizeTimelinePreset(source?.preset);
  const hasExplicitHierarchy = source?.showHierarchy != null;
  const showHierarchy = hasExplicitHierarchy
    ? Boolean(source.showHierarchy)
    : String(source?.timelineRowMode || '').trim().toLowerCase() === 'hierarchy';

  return {
    preset,
    showHierarchy,
    showDependencies: Boolean(source?.showDependencies),
    allowScheduleEdit: Boolean(source?.allowScheduleEdit),
  };
}

export function normalizeChartConfig(rawConfig) {
  const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
  const seriesColors = config?.seriesColors && typeof config.seriesColors === 'object'
    ? Object.entries(config.seriesColors)
      .reduce((acc, [seriesKey, color]) => {
        const key = normalizeString(seriesKey);
        const value = normalizeString(color);
        if (!key || !value) return acc;
        acc[key] = value;
        return acc;
      }, {})
    : {};

  return {
    chartType: String(config.chartType || DEFAULT_CHART_CONFIG.chartType).toLowerCase(),
    aggregation: String(config.aggregation || DEFAULT_CHART_CONFIG.aggregation).toLowerCase(),
    xField: normalizeString(config.xField),
    yField: normalizeString(config.yField),
    groupField: normalizeString(config.groupField),
    showLegend: config.showLegend == null ? true : Boolean(config.showLegend),
    showGrid: config.showGrid == null ? true : Boolean(config.showGrid),
    fontFamily: String(config.fontFamily || DEFAULT_CHART_CONFIG.fontFamily),
    backgroundColor: normalizeString(config.backgroundColor),
    textColor: normalizeString(config.textColor),
    gridColor: normalizeString(config.gridColor),
    yAxisFormat: String(config.yAxisFormat || DEFAULT_CHART_CONFIG.yAxisFormat).toLowerCase(),
    currencyCode: String(config.currencyCode || DEFAULT_CHART_CONFIG.currencyCode).trim().toUpperCase(),
    xDateBin: String(config.xDateBin || DEFAULT_CHART_CONFIG.xDateBin).toLowerCase(),
    seriesColors,
  };
}

export function normalizeBoardEnumOptions(rawOptions) {
  const config = rawOptions && typeof rawOptions === 'object' ? rawOptions : {};
  return {
    fieldKey: normalizeString(config.fieldKey),
    optionOrderIds: Array.isArray(config.optionOrderIds)
      ? [...new Set(config.optionOrderIds
        .map((value) => normalizeString(value))
        .filter(Boolean))]
      : [],
    sortMode: normalizeEnumOptionsViewSortMode(config.sortMode),
    hiddenOptionIds: [],
  };
}

function normalizeSchedulerSlotIncrement(value) {
  const normalized = normalizeString(value);
  if (normalized === '15' || normalized === '30' || normalized === '60') return normalized;
  return DEFAULT_SCHEDULER_CONFIG.slotIncrementMinutes;
}

export function normalizeSchedulerConfig(rawConfig) {
  const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};

  return {
    scheduledType: normalizeString(config?.scheduledType),
    scheduledField: normalizeString(config?.scheduledField),
    slotIncrementMinutes: normalizeSchedulerSlotIncrement(config?.slotIncrementMinutes),
    scheduledFilters: Array.isArray(config?.scheduledFilters) ? config.scheduledFilters : [],
    scheduledProperties: Array.isArray(config?.scheduledProperties) ? config.scheduledProperties : [],
  };
}

function normalizeFiltersWithJoins(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    joinToNext: normalizeFilterJoin(row?.joinToNext),
  }));
}

function normalizePropertiesFromView(view) {
  const next = Array.isArray(view?.properties) ? [...view.properties] : [];
  if (
    getViewIncludeSubtitle(view)
    && !next.includes(DEFAULT_SUBTITLE_FIELD_KEY)
    && !next.includes(LEGACY_SUBTITLE_FIELD_KEY)
  ) {
    next.push(DEFAULT_SUBTITLE_FIELD_KEY);
  }
  if (view?.includeCover && !next.includes('cover')) next.push('cover');
  return next;
}

/**
 * Hydrates a shared view editor draft from a persisted view record.
 */
export function hydrateViewEditorDraft(view, {
  initialViewType = 'LIST',
  isEditMode = false,
  key = '',
} = {}) {
  if (isEditMode && view) {
    const schedulerConfig = normalizeSchedulerConfig(view?.schedulerConfig);
    const timelineFlags = resolveTimelinePresetFlags(view);
    return {
      key: normalizeString(key || view?.key || view?.id) || generateKey(),
      title: view?.title || '',
      titleTouched: true,
      description: normalizeString(view?.description),
      type: view?.type || 'LIST',
      boardField: view?.boardField || '',
      boardLayout: normalizeBoardLayout(view?.boardLayout),
      boardEnumOptions: normalizeBoardEnumOptions(view?.boardEnumOptions),
      viewGroupId: normalizeString(view?.viewGroupId) || '',
      properties: normalizePropertiesFromView(view),
      filters: normalizeFiltersWithJoins(view?.filters),
      sorts: Array.isArray(view?.sorts) ? view.sorts : [],
      includeDescendants: Boolean(view?.includeDescendants),
      timelineField: view?.timelineField || '',
      timelineRowMode: view?.timelineRowMode || 'flat',
      timelineGroupField: view?.timelineGroupField || '',
      showUnscheduled: view?.showUnscheduled == null ? true : Boolean(view?.showUnscheduled),
      preset: timelineFlags.preset,
      showHierarchy: timelineFlags.showHierarchy,
      showDependencies: timelineFlags.showDependencies,
      allowScheduleEdit: timelineFlags.allowScheduleEdit,
      calendarField: normalizeString(view?.calendarField),
      chartConfig: normalizeChartConfig(view?.chartConfig),
      scheduledType: schedulerConfig.scheduledType,
      scheduledField: schedulerConfig.scheduledField,
      slotIncrementMinutes: schedulerConfig.slotIncrementMinutes,
      scheduledFilters: normalizeFiltersWithJoins(schedulerConfig.scheduledFilters),
      scheduledProperties: schedulerConfig.scheduledProperties,
    };
  }

  // Create mode — optionally seed from a caller draft (still a new view).
  const seed = view && typeof view === 'object' ? view : null;
  const seededType = normalizeString(seed?.type || initialViewType).toUpperCase() || 'LIST';
  const seededTitle = normalizeString(seed?.title);
  const seededBoardField = normalizeString(seed?.boardField);
  const seededCalendarField = normalizeString(seed?.calendarField || seed?.dateField);
  const seededTimelineFlags = resolveTimelinePresetFlags(seed);
  const isGanttSeed = seededType === 'TIMELINE' && seededTimelineFlags.preset === 'gantt';

  return {
    key: normalizeString(key) || generateKey(),
    title: seededTitle,
    titleTouched: Boolean(seededTitle),
    description: normalizeString(seed?.description),
    type: seededType,
    boardField: seededBoardField,
    boardLayout: normalizeBoardLayout(seed?.boardLayout),
    boardEnumOptions: normalizeBoardEnumOptions(
      seed?.boardEnumOptions || (seededBoardField ? { fieldKey: seededBoardField } : null)
    ),
    viewGroupId: normalizeString(seed?.viewGroupId),
    properties: Array.isArray(seed?.properties) ? [...seed.properties] : [],
    filters: normalizeFiltersWithJoins(seed?.filters),
    sorts: Array.isArray(seed?.sorts) ? seed.sorts : [],
    includeDescendants: Boolean(seed?.includeDescendants),
    timelineField: normalizeString(seed?.timelineField),
    timelineRowMode: seed?.timelineRowMode || 'flat',
    timelineGroupField: normalizeString(seed?.timelineGroupField),
    showUnscheduled: seed?.showUnscheduled == null ? true : Boolean(seed?.showUnscheduled),
    preset: seededTimelineFlags.preset,
    showHierarchy: seed?.showHierarchy != null
      ? Boolean(seed.showHierarchy)
      : (isGanttSeed || seededTimelineFlags.showHierarchy),
    showDependencies: seed?.showDependencies != null
      ? Boolean(seed.showDependencies)
      : isGanttSeed,
    allowScheduleEdit: Boolean(seed?.allowScheduleEdit),
    calendarField: seededCalendarField,
    chartConfig: normalizeChartConfig(seed?.chartConfig),
    scheduledType: DEFAULT_SCHEDULER_CONFIG.scheduledType,
    scheduledField: DEFAULT_SCHEDULER_CONFIG.scheduledField,
    slotIncrementMinutes: DEFAULT_SCHEDULER_CONFIG.slotIncrementMinutes,
    scheduledFilters: DEFAULT_SCHEDULER_CONFIG.scheduledFilters,
    scheduledProperties: DEFAULT_SCHEDULER_CONFIG.scheduledProperties,
  };
}

export function createEmptyViewEditorDraft(initialViewType = 'LIST') {
  return hydrateViewEditorDraft(null, { initialViewType });
}
